import { useCallback, useEffect, useRef, useState } from 'react';
import { FaBell } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useFeature } from '../../hooks/useFeature';
import { useLogin } from '../../hooks/useLogin';
import { useUpdates } from '../../hooks/useUpdates';
import {
  useMessageThread, useUnreadMessageCount, useMarkMessagesRead, invalidateMessages,
} from '../../api/queryHooks/messages';
import { isStaff } from '../../auth/roles';
import ChatPanel from '../chat/ChatPanel';
import { useChatTopOffset } from '../chat/useChatTopOffset';
import '../chat/chat.scss';

/**
 * The staff half of internal messaging: reminders that Relations fired,
 * delivered as system messages into the signed-in user's own thread (spec
 * decision 6). Read-only — staff cannot post into this thread, and the
 * backend refuses if they try.
 *
 * Mounted in TopNav beside ChatIndicator, which AdminLayout renders in its
 * sticky top row; the two are mutually exclusive in practice (ChatIndicator
 * hides for every staff role, this hides for everyone else) but neither
 * depends on that.
 *
 * The `/messages` endpoints pass with `messaging` OR `relations`
 * (require_any_feature!), so the gate here mirrors the backend's: polling
 * without one would 403 on every admin page load.
 */
const ReminderBell = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));
  const location = useLocation();

  const { userData, isLoggedIn, isGuest } = useLogin();
  const relationsEnabled = useFeature('relations');
  const messagingEnabled = useFeature('messaging');
  const visible = isLoggedIn && !isGuest && isStaff(userData?.roles) && (relationsEnabled || messagingEnabled);

  const { data: thread, isError: threadError } = useMessageThread(open);
  const { data: unread } = useUnreadMessageCount(visible);
  const markRead = useMarkMessagesRead();

  // Opening the panel is the read receipt. Deliberately simpler than
  // ChatIndicator's tab-visibility dance: a reminder bell is only ever opened
  // by a deliberate click, and POST /messages/read is an idempotent
  // update_all. The effect also re-fires — and calls markRead again — if the
  // count changes while the panel is already open (e.g. another reminder
  // fires while it's up, or a backgrounded tab's poll surfaces a new count
  // once foregrounded); that residual extra call is accepted rather than
  // guarded against, since the mutation is idempotent.
  useEffect(() => {
    if (open && (unread?.count ?? 0) > 0) markRead.mutate();
    // markRead is a fresh object every render (TanStack Query); only the two
    // values actually compared belong in the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, unread?.count]);

  useUpdates(
    useCallback((msg) => {
      if (msg.type === 'message.created' || msg.type === 'message.seen') invalidateMessages();
    }, []),
    // A reminder that fired while the socket was down is in neither cache
    // until something refetches.
    useCallback(() => invalidateMessages(), []),
  );

  // On mobile the panel is a full-height sheet that must sit directly below
  // the sticky top nav — see useChatTopOffset (shared with ChatIndicator,
  // which never mounts for staff, so this can't rely on that instance).
  useChatTopOffset(open, ref);

  // The card's `Open` link does a client-side navigation without unmounting
  // TopNav, so without this the fixed/full-screen panel would stay parked
  // over the destination page. Skip the first run so mounting the bell
  // itself (at whatever route the shop happens to be on) doesn't immediately
  // close a panel nobody opened yet.
  const mountedAtLocation = useRef(false);
  useEffect(() => {
    if (!mountedAtLocation.current) {
      mountedAtLocation.current = true;
      return;
    }
    setOpen(false);
  }, [location.pathname, location.search]);

  if (!visible) return null;
  const count = unread?.count ?? 0;
  const toggle = () => setOpen((o) => !o);

  return (
    <div ref={ref}>
      <div
        className="chat-indicator"
        onClick={toggle}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle()}
        aria-label={t('admin.reminders.bell')}
        role="button"
        tabIndex={0}
      >
        <div className="chat-icon-wrapper">
          <FaBell size={20} />
          {count > 0 && (
            <span className="chat-badge" role="status" aria-label={t('admin.reminders.unread_badge', { count })}>
              {count}
            </span>
          )}
        </div>
      </div>
      {open && (
        <ChatPanel
          messages={thread ?? []}
          readOnly
          title={t('admin.reminders.title')}
          emptyLabel={t('admin.reminders.empty')}
          error={threadError}
        />
      )}
    </div>
  );
};

export default ReminderBell;
